// Üzenet-lista (communication üzenőfal, BELSŐ nézet) — a KÖZÖS DataTable-re épül (a clients.index / meetings /
// tickets a referencia-minta): szerver-oldali (BFF) tárgy/tartalom-kereső (300 ms debounce) +
// típus-szűrő (internal/external) + darabszám-meta a toolbarban, szerver-rendezés (fix kiküldés-dátum kulcs,
// irány-váltás), végtelen görgetés (100/lap). A /messages BFF-végpontról fogyaszt (busz-sorok + küldő-/
// címzett-név feloldva + olvasás-/melléklet-/link-szám + readByMe; a communication INTERNAL namespace → az
// Odin RLS internal-mintája izolál, az ops-token read:all → see-all). Page-gate: `messages.view`.
//
// WRITE-VERTIKUM (messages.md írás-vertikum terv — forms-migration):
//   • „Új üzenet" (messages.manage) → MessageComposeSheet: típus + EXPLICIT címzés (Mindenki/Minden ügyfél
//     rádió VAGY célzott multiselect a /messages/refs-ből) + tartalom + „Küldés"/„Időzítés" (helyi → UTC).
//   • SOR-AKCIÓK CSAK A SAJÁT sorokon (createdBy = aktuális identity, a UI is rejti másén; a BFF a fal):
//     draft → „Küldés most" (Confirm) · „Időzítés"; scheduled → „Küldés most" · „Átidőzítés" · „Visszavonás"
//     (Confirm; 409 = a dispatcher megelőzte → conflict-toast + lista-invalidálás); minden státusz →
//     „Szerkesztés" (5 mező; sent-nél figyelmeztetés + revízió a BFF-en). Minden státusz-átmenet OCC-s
//     (If-Match a sor-verzióval) + optimista státusz-átírás rollbackkel.
//   • OLVASOTTSÁG: sor-kattintás → MessageDetailSheet (szanitált HTML) + automatikus messageMarkRead
//     (optimista readByMe=true, hibánál rollback, NÉMA — nincs toast); fejléc-gomb „Mind olvasottnak jelöl"
//     (messageMarkAllRead, optimista). Az olvasatlan sor jelzése: pont + félkövér tárgy (readByMe=false).
//
// KIHAGYVA (nincs mögötte Atlas-backend / külön increment — jelezve):
//   • MELLÉKLET-feltöltés (fájl-fázis) és rich-text editor (a törzs sima Textarea → minimális HTML) — külön increment.
//   • A fiók/szolgáltatás/részleg címzettek NEVE a lista-cellában (kereszt-namespace) — a v1 darabszámmal adja.
//   • Küldő-/olvasottság-SZŰRŐ (a legacy per-oszlop szabad-szöveg szűrői): a BFF a `q`-t tárgyra+tartalomra illeszti.
//   • „Szerkesztve" jelzés a LISTA-sorban — a MessageRowSchema nem hordoz editedAt-et (contract KÉSZ, nem bővítjük
//     itt); a jelzés a detail-sheetben él.
import { type MessageRowSchema } from '@atlas/contracts';
import {
  ArchiveBoxArrowDownIcon,
  ArchiveBoxIcon,
  ArchiveBoxXMarkIcon,
  BookmarkIcon,
  BookmarkSlashIcon,
  ClockIcon,
  EnvelopeIcon,
  EnvelopeOpenIcon,
  InboxArrowDownIcon,
  NoSymbolIcon,
  PaperAirplaneIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import {
  type InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { type TFunction } from 'i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { MessageComposeSheet } from '@/components/messages/message-compose-sheet';
import { MessageDetailSheet } from '@/components/messages/message-detail-sheet';
import { MessageEditSheet } from '@/components/messages/message-edit-sheet';
import { MessageRecipientsCell } from '@/components/messages/message-recipients-cell';
import { MessageScheduleDialog } from '@/components/messages/message-schedule-dialog';
import { MessageStatusBadge } from '@/components/messages/message-status-badge';
import { AppShell } from '@/components/shared/app-shell';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { DetailsExpander } from '@/components/shared/details-expander';
import { EmptyState } from '@/components/shared/empty-state';
import { ListErrorState } from '@/components/shared/list-error-state';
import { ListNewButton } from '@/components/shared/list-new-button';
import { EditRowAction, RowActionButton, RowActions } from '@/components/shared/list-row-actions';
import { ListToolbar } from '@/components/shared/list-toolbar';
import { PersonChip } from '@/components/shared/person-chip';
import { Button } from '@/components/ui/button';
import { Tabs, TabsCount, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Text } from '@/components/ui/text';
import { messageKeys } from '@/features/messages/api';
import { localInputToIso } from '@/features/messages/form';
import { useDeepLinkSheet } from '@/hooks/use-deep-link-sheet';
import { useListFilters } from '@/hooks/use-list-filters';
import { api } from '@/lib/api';
import { requireAuth } from '@/lib/auth';
import { formatDay } from '@/lib/format-datetime';
import { useEntityMutation } from '@/lib/use-entity-mutation';
import { usePermissions } from '@/lib/use-permissions';
import { isForbiddenStatus } from '@/lib/error-status';

// Deep-link: `?open=<id>` → a részletnézet-sheet a megadott üzenetre nyílik (a command palette „2. rungja").
// A SZŰRŐK is search-paramok (a state-konvenció szerint: a szűrt nézet legyen linkelhető és F5-biztos) — a
// route-nak ezért ISMERNIE kell a kulcsokat, különben a router kiszűri őket. A default-értékű kulcs nem kerül
// az URL-be (lásd `useListFilters`), tehát a szűretlen lista URL-je változatlanul `/messages`.
const messagesSearchSchema = z.object({
  open: z.string().optional(),
  q: z.string().optional(),
  // a kategória-szűrő kulcsa — HIÁNYZOTT, ezért a router kiszűrte, és a szűrő nem maradt meg az URL-ben
  category: z.string().optional(),
  // OLVASÓ-OLDALI ARCHÍVUM nézet (`only` → csak a saját archiváltak)
  archived: z.string().optional(),
  // KITŰZÖTT nézet (`only` → csak a nekem szóló kitűzöttek — a kezdőlap „+N további kitűzött" linkje ide visz)
  pinned: z.string().optional(),
  type: z.string().optional(),
  direction: z.string().optional(),
  order: z.string().optional(),
});

export const Route = createFileRoute('/messages')({
  validateSearch: (search) => messagesSearchSchema.parse(search),
  beforeLoad: () => requireAuth(),
  component: MessagesRoute,
});

// Route-wrapper: a deep-link search-paramot itt olvassuk + a param-törlést itt navigáljuk (a lista-oldal router-hook-mentes).
function MessagesRoute(): JSX.Element {
  const { open } = Route.useSearch();
  const navigate = useNavigate();
  const clearDeepLink = useCallback(() => {
    void navigate({
      to: '/messages',
      search: (prev) => ({ ...prev, open: undefined }),
      replace: true,
    });
  }, [navigate]);
  return <MessagesPage deepLinkOpenId={open} onClearDeepLink={clearDeepLink} />;
}

type MessageRow = z.infer<typeof MessageRowSchema>;
type ListPage = Awaited<ReturnType<typeof api.messages.query>>;

const PAGE_SIZE = 100;
const ALL = '__all__';

export interface MessagesPageProps {
  /** Deep-link: a `?open=` paraméterből érkező üzenet-id (a részletnézet-sheet erre nyílik, ha a sor betöltött). */
  deepLinkOpenId?: string | undefined;
  /** A deep-link param törlése (a sheet záráskor hívjuk). */
  onClearDeepLink?: () => void;
}

// A legacy üzenet-típus enum (adat-parity a régi CRM-mel — messages.md). MINDKETTŐ CRM-en belüli fal.
const TYPE_OPTIONS = ['internal', 'external'];
/** A lap két tartalom-fajtája — a KATEGÓRIA-szűrő értékei (a besorolás szerver-oldali). */
const CATEGORY_OPTIONS = ['message', 'system'] as const;
// A legacy státuszok, amiket a tárgy-cellában külön chippel emelünk ki (a `sent` az alap → nincs chip).
const HIGHLIGHTED_STATUSES = new Set(['draft', 'scheduled']);

/**
 * Az üzenet „Részletek" sorai: KATEGÓRIA · TÍPUS · MELLÉKLETEK · LINKEK · OLVASÁS. Tiszta függvény (a `t`
 * injektálva), mert ugyanezt a listát a DESKTOP kibontó és a MOBIL-kártya `cardExtraItems`-e is használja —
 * egy helyen dől el, mi tartozik a részletek közé (nem tud csendben elcsúszni a két megjelenítés).
 *
 * MIÉRT ITT: a user-döntés szerint a melléklet-szám NEM kap dedikált oszlopot (másodlagos infó), a kategória
 * pedig ÚJ (üzenet vs rendszerértesítés) — mindkettő a részletek helye.
 */
function messageDetailRows(m: MessageRow, t: TFunction): { label: string; value: string }[] {
  return [
    // A KATEGÓRIA áll elöl (user-döntés): az „üzenet vs. rendszerértesítés" a durvább bontás — az ember
    // ELŐBB azt olvassa le, MI ez a sor, és csak utána a finomabb belső/külső típust.
    { label: t('messages.detailsCategory'), value: t(`messages.category.${m.category}`) },
    {
      label: t('messages.detailsType'),
      value: t(`messages.typeLabel.${m.type}`, { defaultValue: m.type }),
    },
    { label: t('messages.detailsAttachments'), value: String(m.attachmentCount) },
    { label: t('messages.detailsLinks'), value: String(m.linkCount) },
    { label: t('messages.detailsReads'), value: String(m.readCount) },
  ];
}

export function MessagesPage({
  deepLinkOpenId,
  onClearDeepLink,
}: MessagesPageProps = {}): JSX.Element {
  const { t, i18n } = useTranslation();
  const { can, isLoading: permsLoading } = usePermissions();
  const canView = can('messages.view');
  // Write-akciók jog-kapuja (a BFF minden write-endpointon enforce-olja — itt csak UX-rejtés).
  const canManage = can('messages.manage');
  const queryClient = useQueryClient();

  // Az aktuális identity (a session `sub`-ja) — a SAJÁT sorok owner-gate-je (senderId = createdBy).
  const sessionQ = api.session.useQuery(['session'], {});
  const mySub = sessionQ.data?.status === 200 ? sessionQ.data.body.sub : null;

  /**
   * A SZŰRŐK az URL-ben (linkelhető + F5-biztos nézet). A default-értékek egyben a kulcs-készlet:
   *  • `direction` — a legacy „Bejövő"/„Kimenő" fülei (a „mind" érték csak URL-ből érhető el, fül nincs rá); a legacy defaultja a BEJÖVŐ volt
   *    (az üzenőfalra belépve azt kell látni, ami neked jött) — ezt tartjuk,
   *  • `order` — a szerver-rendezés IRÁNYA (a kulcs fixen a kiküldés-dátum, a BFF azon rendez).
   */
  const { filters: urlFilters, setFilters } = useListFilters({
    q: '',
    // KATEGÓRIA (üzenet / rendszerértesítés) — a szűrők ELSŐ mezője; a `__all__` az „mind".
    category: ALL,
    type: ALL,
    direction: 'incoming',
    order: 'desc',
    // ARCHÍVUM-nézet: '' = az alap (archiváltak nélkül), 'only' = csak a saját archiváltak
    archived: '',
    // KITŰZÖTT-nézet: '' = az alap, 'only' = csak a nekem szóló kitűzöttek (a kitűzés ideje szerint)
    pinned: '',
  });
  const archivedView = urlFilters.archived === 'only';
  const pinnedView = urlFilters.pinned === 'only';
  const q = urlFilters.q;
  const category = urlFilters.category;
  const type = urlFilters.type;
  const direction = urlFilters.direction as 'incoming' | 'outgoing' | typeof ALL;
  const order = urlFilters.order as 'asc' | 'desc';
  // A beviteli mező AZONNALI (lokális) állapota — az URL-be a debounce-olt érték kerül (nem minden leütés).
  const [searchInput, setSearchInput] = useState(q);

  // Write-vertikum UI-állapot: compose/edit/detail sheetek + a megerősítendő sor-akciók célpontjai.
  const [composeOpen, setComposeOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<MessageRow | null>(null);
  const [detailTarget, setDetailTarget] = useState<MessageRow | null>(null);
  const [sendTarget, setSendTarget] = useState<MessageRow | null>(null);
  const [scheduleTarget, setScheduleTarget] = useState<MessageRow | null>(null);
  const [unscheduleTarget, setUnscheduleTarget] = useState<MessageRow | null>(null);

  // Kereső-debounce (300 ms) — az URL-t (és így a lista-lekérdezést) a MEGÁLLAPODOTT érték írja.
  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === q) return;
    const timer = setTimeout(() => setFilters({ q: trimmed }), 300);
    return () => clearTimeout(timer);
  }, [searchInput, q, setFilters]);

  // Page-gate: messages.view nélkül meg sem kérdezzük a szervert (a BFF is 403-at adna) → azonnali jog-hiba.
  const gated = !permsLoading && !canView;

  // a cache-kulcs MINDEN lekérdezés-paramétert hordoz (az archívum-nézet nélküle a régi lapot mutatná)
  const filters = {
    q,
    category,
    type,
    order,
    direction,
    archived: archivedView,
    pinned: pinnedView,
  };
  const listKey = messageKeys.list(filters);

  const infinite = useInfiniteQuery({
    queryKey: listKey,
    enabled: !gated,
    queryFn: ({ pageParam }) =>
      api.messages.query({
        query: {
          q: q || undefined,
          direction: direction === ALL ? undefined : direction,
          category: category === ALL ? undefined : (category as 'message' | 'system'),
          type: type === ALL ? undefined : type,
          order,
          ...(archivedView ? { archived: 'only' as const } : {}),
          ...(pinnedView ? { pinned: 'only' as const } : {}),
          limit: PAGE_SIZE,
          offset: pageParam,
        },
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.status === 200 ? (lastPage.body.nextOffset ?? undefined) : undefined,
  });

  const pages = useMemo(() => infinite.data?.pages ?? [], [infinite.data]);
  const rows = useMemo(
    () => pages.flatMap((p) => (p.status === 200 ? p.body.messages : [])),
    [pages],
  );
  const okPage = pages.find((p) => p.status === 200);
  const total = okPage?.status === 200 ? okPage.body.total : 0;
  const hasNoSuccess = pages.length > 0 && !pages.some((p) => p.status === 200);
  const isError = gated || infinite.isError || hasNoSuccess;
  const isForbidden = gated || pages.some((p) => isForbiddenStatus(p.status));
  const hasFilters = q !== '' || type !== ALL || category !== ALL;
  const directionCounts = okPage?.status === 200 ? okPage.body.directions : undefined;
  const hasUnread = rows.some((m) => !m.readByMe);

  // Egyetlen rendezhető oszlop (Dátum): a fejléc-kattintás CSAK az irányt váltja (a kulcs fix kiküldés-dátum).
  const toggleOrder = useCallback(
    () => setFilters({ order: order === 'asc' ? 'desc' : 'asc' }),
    [order, setFilters],
  );
  const { fetchNextPage } = infinite;
  const loadMore = useCallback(() => void fetchNextPage(), [fetchNextPage]);

  // Optimista sor-átírás a lapozott lista-cache-ben (a rollback a mutáció-hookban).
  const rewriteRows = useCallback(
    (
      old: InfiniteData<ListPage> | undefined,
      rewrite: (m: MessageRow) => MessageRow,
    ): InfiniteData<ListPage> | undefined => {
      if (!old) return old;
      return {
        ...old,
        pages: old.pages.map((p) =>
          p.status === 200
            ? { ...p, body: { ...p.body, messages: p.body.messages.map(rewrite) } }
            : p,
        ),
      };
    },
    [],
  );

  // Optimista sor-ELTÁVOLÍTÁS (archiválás: a sor az aktuális nézetből eltűnik) — a darabszám is csökken.
  const removeRow = useCallback(
    (old: InfiniteData<ListPage> | undefined, id: string): InfiniteData<ListPage> | undefined => {
      if (!old) return old;
      return {
        ...old,
        pages: old.pages.map((p) =>
          p.status === 200 && p.body.messages.some((m) => m.id === id)
            ? {
                ...p,
                body: {
                  ...p.body,
                  messages: p.body.messages.filter((m) => m.id !== id),
                  total: Math.max(0, p.body.total - 1),
                  // a fül-darabszám is kövesse: a saját sor a kimenő, a másé a bejövő fülön számolt
                  directions: ((): { incoming: number; outgoing: number } => {
                    const own = p.body.messages.find((m) => m.id === id)?.senderId === mySub;
                    const d = p.body.directions;
                    return own
                      ? { ...d, outgoing: Math.max(0, d.outgoing - 1) }
                      : { ...d, incoming: Math.max(0, d.incoming - 1) };
                  })(),
                },
              }
            : p,
        ),
      };
    },
    [mySub],
  );

  // „Küldés most" — draft/scheduled → sent (OCC-claim, If-Match); optimista státusz-átírás.
  const sendMutation = useEntityMutation<{ id: string; version: number }, InfiniteData<ListPage>>({
    mutationFn: ({ id, version }) =>
      api.messageSend.mutation({ params: { id }, headers: { 'if-match': String(version) } }),
    invalidateKeys: [messageKeys.lists(), messageKeys.details()],
    optimistic: {
      queryKey: listKey,
      update: (old) =>
        rewriteRows(old, (m) =>
          m.id === sendTarget?.id
            ? { ...m, status: 'sent', sentAt: new Date().toISOString(), scheduledAt: null }
            : m,
        ),
    },
    messages: {
      success: t('toast:messages.sent'),
      conflict: t('toast:messages.conflict'),
      forbidden: t('toast:messages.forbidden'),
      validation: t('toast:messages.validation'),
      error: t('toast:messages.error'),
    },
  });

  // Időzítés / átidőzítés — draft/scheduled → scheduled (jövőbeli időpont; OCC).
  const scheduleMutation = useEntityMutation<
    { id: string; version: number; scheduledAtLocal: string },
    InfiniteData<ListPage>
  >({
    mutationFn: ({ id, version, scheduledAtLocal }) =>
      api.messageSchedule.mutation({
        params: { id },
        body: { scheduledAt: localInputToIso(scheduledAtLocal) },
        headers: { 'if-match': String(version) },
      }),
    invalidateKeys: [messageKeys.lists(), messageKeys.details()],
    optimistic: {
      queryKey: listKey,
      update: (old) =>
        rewriteRows(old, (m) => (m.id === scheduleTarget?.id ? { ...m, status: 'scheduled' } : m)),
    },
    messages: {
      success: t('toast:messages.scheduled'),
      conflict: t('toast:messages.conflict'),
      forbidden: t('toast:messages.forbidden'),
      validation: t('toast:messages.validation'),
      error: t('toast:messages.error'),
    },
  });

  // Időzítés visszavonása — scheduled → draft; 409 = a dispatcher megelőzte („már elküldve" → conflict-toast).
  const unscheduleMutation = useEntityMutation<
    { id: string; version: number },
    InfiniteData<ListPage>
  >({
    mutationFn: ({ id, version }) =>
      api.messageUnschedule.mutation({ params: { id }, headers: { 'if-match': String(version) } }),
    invalidateKeys: [messageKeys.lists(), messageKeys.details()],
    optimistic: {
      queryKey: listKey,
      update: (old) =>
        rewriteRows(old, (m) =>
          m.id === unscheduleTarget?.id ? { ...m, status: 'draft', scheduledAt: null } : m,
        ),
    },
    messages: {
      success: t('toast:messages.unscheduled'),
      conflict: t('toast:messages.conflict'),
      forbidden: t('toast:messages.forbidden'),
      validation: t('toast:messages.validation'),
      error: t('toast:messages.error'),
    },
  });

  // KITŰZÉS / levétel — optimista pinnedAt; a kitűzött körüzenet a kezdőlapon jelenik meg (owner + OCC a BFF-en).
  const pinMutation = useEntityMutation<
    { id: string; version: number; pinned: boolean },
    InfiniteData<ListPage>
  >({
    mutationFn: ({ id, version, pinned }) =>
      pinned
        ? api.messagePin.mutation({ params: { id }, headers: { 'if-match': String(version) } })
        : api.messageUnpin.mutation({ params: { id }, headers: { 'if-match': String(version) } }),
    invalidateKeys: [messageKeys.lists(), messageKeys.details()],
    optimistic: {
      queryKey: listKey,
      update: (old, vars) =>
        rewriteRows(old, (m) =>
          m.id === vars.id ? { ...m, pinnedAt: vars.pinned ? new Date().toISOString() : null } : m,
        ),
    },
    messages: {
      success: t('toast:messages.pinUpdated'),
      conflict: t('toast:messages.conflict'),
      forbidden: t('toast:messages.forbidden'),
      validation: t('toast:messages.pinValidation'),
      error: t('toast:messages.error'),
    },
  });

  // OLVASÓ-OLDALI ARCHIVÁLÁS / visszaállítás — a sor az aktuális nézetből azonnal eltűnik (az alap-nézet az
  // archiváltak nélkül, az Archivált-nézet csak velük él); a többi olvasónál az üzenet változatlan.
  const archiveMutation = useEntityMutation<
    { id: string; archived: boolean },
    InfiniteData<ListPage>
  >({
    mutationFn: ({ id, archived }) =>
      archived
        ? api.messageArchive.mutation({ params: { id } })
        : api.messageUnarchive.mutation({ params: { id } }),
    invalidateKeys: [messageKeys.lists()],
    optimistic: {
      queryKey: listKey,
      update: (old, vars) => removeRow(old, vars.id),
    },
    messages: {
      success: t('toast:messages.archiveUpdated'),
      conflict: t('toast:messages.conflict'),
      forbidden: t('toast:messages.forbidden'),
      validation: t('toast:messages.validation'),
      error: t('toast:messages.error'),
    },
  });

  // „Mind olvasottnak jelöl" — optimista: minden sor readByMe=true (a BFF csak a bejövő olvasatlanokra ír).
  const markAllReadMutation = useEntityMutation<Record<string, never>, InfiniteData<ListPage>>({
    mutationFn: () => api.messageMarkAllRead.mutation({}),
    invalidateKeys: [messageKeys.lists()],
    optimistic: {
      queryKey: listKey,
      update: (old) => rewriteRows(old, (m) => (m.readByMe ? m : { ...m, readByMe: true })),
    },
    messages: {
      success: t('toast:messages.allRead'),
      conflict: t('toast:messages.conflict'),
      forbidden: t('toast:messages.forbidden'),
      validation: t('toast:messages.validation'),
      error: t('toast:messages.error'),
    },
  });

  // Automatikus olvasottnak jelölés a detail-nyitáskor — NÉMA (nincs toast), optimista + rollback.
  const markReadMutation = useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const res = await api.messageMarkRead.mutation({ params: { id } });
      if (res.status >= 400) throw new Error(`HTTP ${String(res.status)}`);
      return res;
    },
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({ queryKey: listKey });
      const previous = queryClient.getQueryData<InfiniteData<ListPage>>(listKey);
      queryClient.setQueryData<InfiniteData<ListPage>>(listKey, (old) =>
        rewriteRows(old, (m) => (m.id === id ? { ...m, readByMe: true } : m)),
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      // Rollback — az olvasatlan-jelölés visszaáll (a mark-read idempotens, a következő nyitás újrapróbálja).
      if (context?.previous) queryClient.setQueryData(listKey, context.previous);
    },
  });

  // Sor-kattintás: detail-sheet + automatikus mark-read (csak ha még olvasatlan — idempotens upsert).
  const openDetail = (m: MessageRow): void => {
    setDetailTarget(m);
    if (!m.readByMe) markReadMutation.mutate({ id: m.id });
  };

  // Deep-link: a `?open=<id>` üzenetre nyitjuk a részletnézetet, amint a sor betöltött (a sor-kattintással azonos
  // viselkedés: nyitás + auto mark-read). Ha a sor nincs a betöltött lapon → graceful no-op.
  useDeepLinkSheet<MessageRow>({
    id: deepLinkOpenId,
    rows,
    getId: (m) => m.id,
    onOpen: (m) => openDetail(m),
  });

  function confirmSend(): void {
    if (!sendTarget) return;
    sendMutation.mutate(
      { id: sendTarget.id, version: sendTarget.version },
      { onSettled: () => setSendTarget(null) },
    );
  }

  function confirmSchedule(scheduledAtLocal: string): void {
    if (!scheduleTarget) return;
    scheduleMutation.mutate(
      { id: scheduleTarget.id, version: scheduleTarget.version, scheduledAtLocal },
      { onSettled: () => setScheduleTarget(null) },
    );
  }

  function confirmUnschedule(): void {
    if (!unscheduleTarget) return;
    unscheduleMutation.mutate(
      { id: unscheduleTarget.id, version: unscheduleTarget.version },
      { onSettled: () => setUnscheduleTarget(null) },
    );
  }

  // Halvány em-dash az üres tartalmi cellákhoz.
  const emDash = <span className="text-muted-foreground">—</span>;
  const locale = i18n.language;

  const columns: DataTableColumn<MessageRow>[] = [
    {
      // 1. Tárgy — a tárgy (itemTitle, kártya-fejléc) + típus-badge + a kiemelt státusz (draft/scheduled) badge.
      //    Olvasatlan (readByMe=false) sor: kis pont a tárgy előtt + félkövér tárgy (a meglévő lista-stílushoz).
      id: 'subject',
      header: t('messages.columns.subject'),
      cardLabel: t('messages.columns.subject'),
      width: 'minmax(0,2.4fr)',
      multiline: true,
      // MOBIL-kártya: a részlet-párok a kártya EGYETLEN „Részletek" rácsába (a kártyán a kibontót a kártya
      // adja — a desktop-kibontó ott nem renderel; lásd a `DetailsExpander` fejlécét).
      cardExtraItems: (m) =>
        messageDetailRows(m, t).map((r) => ({ label: r.label, value: r.value })),
      cell: (m) => (
        <div className="min-w-0 space-y-1.5">
          <div className="flex min-w-0 items-center gap-1.5">
            {/* OLVASATLAN-jelzés CSAK a BEJÖVŐ nézetben (user-döntés, 2026-08-25): a KIMENŐ lista a saját
                kiküldött üzeneteim — ott az „olvasatlan" a feladóra nézve értelmetlen (a `readByMe` a saját
                olvasás-jelölésem, ami egy általam küldött üzenetnél nem információ). A `Mind`-nézetben marad,
                mert az a bejövőket is tartalmazza. */}
            {!m.readByMe && direction !== 'outgoing' && (
              // OLVASATLAN: pirosas BORÍTÉK-ikon (a korábbi zöld pont helyett) — a boríték a „nem olvastam
              // még" konvencionális jelölése, a szín pedig figyelem-jelzés. `role="status"` + aria-label:
              // képernyőolvasóval is hallható, nem csak látható.
              // A SZEMANTIKA a wrapper `span`-en van (`role="status"` + a11y-név), az SVG dekoratív: a
              // Heroicons-komponens magától `aria-hidden`-t kap, tehát az ikonra tett aria-label nem
              // hallatszana (mérve: a képernyőolvasó-fal nem találta meg).
              <span role="status" aria-label={t('messages.unread')} className="flex shrink-0">
                <EnvelopeIcon className="text-destructive size-4" aria-hidden />
              </span>
            )}
            {m.pinnedAt && (
              // KITŰZVE: a kezdőlapon kiemelt körüzenet — ikon + a11y-név (a dekoratív SVG maga aria-hidden)
              <span role="img" aria-label={t('messages.pinned')} className="flex shrink-0">
                <BookmarkIcon className="text-primary size-4" aria-hidden />
              </span>
            )}
            <Text
              variant="itemTitle"
              as="div"
              className={
                m.readByMe || direction === 'outgoing' ? 'truncate' : 'truncate font-semibold'
              }
            >
              {m.subject || emDash}
            </Text>
          </div>
          {/* A kiemelt státusz (piszkozat/időzített) MARAD a cím alatt — az a sor ÁLLAPOTA, nem részlet.
              A típus/kategória/melléklet/olvasás a „Részletek" alá került (user-döntés: a táblában ne
              legyen dedikált melléklet-oszlop). */}
          <DetailsExpander
            rows={messageDetailRows(m, t)}
            trailing={
              HIGHLIGHTED_STATUSES.has(m.status) ? (
                <MessageStatusBadge
                  status={m.status}
                  label={t(`messages.statusLabel.${m.status}`, { defaultValue: m.status })}
                />
              ) : undefined
            }
          />
        </div>
      ),
    },
    {
      // 2. Címzés — szórás-/csoport-badge + NÉVVEL feloldott identity-címzettek (ExpandableCell) — a legacy 1:1.
      id: 'recipients',
      header: t('messages.columns.recipients'),
      cardLabel: t('messages.columns.recipients'),
      width: 'minmax(0,1.8fr)',
      multiline: true,
      // Mobil-kártya: REJTVE — a címzett-cella badge/ExpandableCell, nem fér el egy tömör subtitle-sorban;
      // a kártyán a feladó (sender) viszi a subtitle-t. A desktop-tábla változatlan.
      card: 'hidden',
      cell: (m) => (
        <MessageRecipientsCell
          recipients={m.recipients}
          groups={m.recipientGroups}
          everyone={m.audienceEveryone}
          allClients={m.audienceAllClients}
        />
      ),
    },
    {
      // 4. Küldő — a létrehozó (createdBy identity) avatar + név; hiányzó/ismeretlen küldőnél halvány felirat.
      id: 'sender',
      header: t('messages.columns.sender'),
      cardLabel: t('messages.columns.sender'),
      width: 'minmax(0,1.3fr)',
      // Mobil-kártya: a 2. sor (subtitle) — a feladó neve a kulcs másodlagos azonosító.
      card: 'subtitle',
      cell: (m) =>
        m.senderName ? (
          // Avatar + NYELVHELYES név (a `PersonChip` a név-részekből formáz) — ugyanaz a minta, mint a
          // support-csapatok „Tagok" oszlopában.
          <PersonChip
            name={m.senderName}
            givenName={m.senderGivenName}
            familyName={m.senderFamilyName}
            avatarUrl={m.senderAvatarUrl}
            accentColor={m.senderTeamColor}
            size="sm"
          />
        ) : (
          <span className="text-muted-foreground">{t('messages.unknownSender')}</span>
        ),
    },
    {
      // 5. Dátum — a kiküldés dátuma (rendezhető: sentAt|scheduledAt|createdAt) + az olvasás-szám másodlagos sorként.
      id: 'date',
      header: t('messages.columns.date'),
      sortable: true,
      cardLabel: t('messages.columns.date'),
      width: 'minmax(0,1.1fr)',
      multiline: true,
      // Mobil-kártya: a 2. sor jobb széle (trailing) — a kiküldés dátuma.
      card: 'trailing',
      cell: (m) => {
        const date = formatDay(m.sentAt ?? m.scheduledAt ?? m.createdAt, locale);
        // Az OLVASÁS-szám a „Részletek" alá került (user-döntés) — itt csak a dátum marad.
        // A tipográfia SZÁNDÉKOSAN a KÜLDŐ-oszlop nevével egyezik (a `PersonChip` név-stílusa: `text-sm`
        // örökölt méret, alap szín, normál súly) — a két szomszédos oszlop egy vonalon olvasódik.
        return <span className="block min-w-0 truncate">{date || emDash}</span>;
      },
    },
  ];

  // SOR-AKCIÓK — CSAK a saját (createdBy = aktuális identity) sorokon, messages.manage-dzsel; a státusz szerint:
  // draft → küldés most + időzítés + szerkesztés; scheduled → küldés most + átidőzítés + visszavonás + szerkesztés;
  // sent → szerkesztés. Más userek sorain a slot üres (a BFF owner-checkje a fal). Az „egyéb" akciók a KÖZÖS
  // RowActionButton-nal (a szerkesztés az EditRowAction-nal) → a kinézet mindenhol azonos.
  const rowActions = (m: MessageRow): JSX.Element => {
    const isOwn = mySub !== null && m.senderId === mySub;
    // OLVASÓ-OLDALI archiválás MINDEN olvasónak (messages.view) — a kiküldött üzeneten (a piszkozat nem „bejövő")
    const archiveAction =
      m.status === 'sent' ? (
        <RowActionButton
          label={m.archivedByMe ? t('messages.actions.unarchive') : t('messages.actions.archive')}
          icon={m.archivedByMe ? ArchiveBoxXMarkIcon : ArchiveBoxArrowDownIcon}
          onClick={() => archiveMutation.mutate({ id: m.id, archived: !m.archivedByMe })}
        />
      ) : null;
    if (!isOwn || !canManage) return <RowActions>{archiveAction}</RowActions>;
    const editable = m.status === 'draft' || m.status === 'scheduled' || m.status === 'sent';
    // KITŰZÉS: csak a saját, kiküldött, BELSŐ üzenet (a BFF is ezt kényszeríti — 422)
    const pinnable = m.status === 'sent' && m.type === 'internal';
    return (
      <RowActions>
        {pinnable && (
          <RowActionButton
            label={m.pinnedAt ? t('messages.actions.unpin') : t('messages.actions.pin')}
            icon={m.pinnedAt ? BookmarkSlashIcon : BookmarkIcon}
            onClick={() =>
              pinMutation.mutate({ id: m.id, version: m.version, pinned: !m.pinnedAt })
            }
          />
        )}
        {archiveAction}
        {(m.status === 'draft' || m.status === 'scheduled') && (
          <RowActionButton
            label={t('messages.actions.sendNow')}
            icon={PaperAirplaneIcon}
            onClick={() => setSendTarget(m)}
          />
        )}
        {(m.status === 'draft' || m.status === 'scheduled') && (
          <RowActionButton
            label={
              m.status === 'scheduled'
                ? t('messages.actions.reschedule')
                : t('messages.actions.schedule')
            }
            icon={ClockIcon}
            onClick={() => setScheduleTarget(m)}
          />
        )}
        {m.status === 'scheduled' && (
          <RowActionButton
            label={t('messages.actions.unschedule')}
            icon={NoSymbolIcon}
            onClick={() => setUnscheduleTarget(m)}
          />
        )}
        {editable && (
          <EditRowAction label={t('messages.actions.edit')} onClick={() => setEditTarget(m)} />
        )}
      </RowActions>
    );
  };

  return (
    <AppShell
      // Mobilon a lebegő FAB (primaryAction) kezeli az „Új üzenet"-et — csak manage-joggal. Desktopon a toolbar
      // ListNewButton-ja (lásd lent, `hidden md:block`). A „Mind olvasottnak" fejléc-gomb minden méreten marad.
      primaryAction={
        canManage
          ? {
              icon: <PlusIcon className="size-5 stroke-2" />,
              label: t('messages.new'),
              onClick: () => setComposeOpen(true),
            }
          : undefined
      }
      toolbar={
        // IRÁNY-FÜLEK a kereső FÖLÖTT (a legacy „Bejövő"/„Kimenő" tabjai), darabszám-badge-ekkel
        // — a bevett tab-minta (assets / jogosultságok / jegyek). Alatta a KÖZÖS ListToolbar:
        // tárgy/tartalom-kereső + „Szűrők (N)" popover (típus) + darabszám-meta + akciók.
        <div className="flex flex-col">
          <div className="px-4 pt-2">
            <Tabs value={direction} onValueChange={(v) => setFilters({ direction: v })}>
              <TabsList>
                <TabsTrigger value="incoming">
                  <InboxArrowDownIcon />
                  {t('messages.tabIncoming')}
                  {directionCounts !== undefined && directionCounts.incoming > 0 && (
                    <TabsCount>{directionCounts.incoming}</TabsCount>
                  )}
                </TabsTrigger>
                <TabsTrigger value="outgoing">
                  <PaperAirplaneIcon />
                  {t('messages.tabOutgoing')}
                  {directionCounts !== undefined && directionCounts.outgoing > 0 && (
                    <TabsCount>{directionCounts.outgoing}</TabsCount>
                  )}
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <ListToolbar
            search={{
              value: searchInput,
              onChange: setSearchInput,
              placeholder: t('common.searchPlaceholder'),
            }}
            filters={[
              {
                // KATEGÓRIA az ELSŐ szűrő (user-döntés): a lap két tartalom-fajtája (üzenet /
                // rendszerértesítés) durvább vágás, mint a belső/külső típus — az előbbi jön előre.
                key: 'category',
                label: t('messages.category.label'),
                value: category,
                onChange: (v: string) => setFilters({ category: v }),
                options: CATEGORY_OPTIONS.map((c) => ({
                  value: c,
                  label: t(`messages.category.${c}`),
                })),
                allValue: ALL,
              },
              {
                key: 'type',
                label: t('messages.filterType'),
                value: type,
                onChange: (v: string) => setFilters({ type: v }),
                options: TYPE_OPTIONS.map((ty) => ({
                  value: ty,
                  label: t(`messages.typeLabel.${ty}`, { defaultValue: ty }),
                })),
                allValue: ALL,
              },
            ]}
            meta={
              infinite.isFetching && !infinite.isFetchingNextPage
                ? t('messages.loading')
                : !isError && !infinite.isLoading
                  ? t('messages.countOf', { shown: rows.length, total })
                  : ''
            }
            actions={
              canView ? (
                <div className="flex items-center gap-2">
                  {/* ARCHÍVUM-nézet kapcsoló: a saját archivált üzeneteim (onnan visszaállíthatók) */}
                  <Button
                    variant="outline"
                    aria-pressed={archivedView}
                    // az archív nézet MINDKÉT irányt mutatja (a saját, kimenő üzenetét archiváló szerző is
                    // megtalálja és visszaállíthatja); kilépéskor vissza a bejövőkre
                    onClick={() =>
                      setFilters(
                        archivedView
                          ? { archived: '', direction: 'incoming' }
                          : { archived: 'only', pinned: '', direction: ALL },
                      )
                    }
                    className={archivedView ? 'bg-accent text-foreground' : undefined}
                  >
                    <ArchiveBoxIcon className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('messages.archivedView')}</span>
                  </Button>
                  {/* KITŰZÖTT-nézet kapcsoló: a nekem szóló kitűzött közlemények (mindkét irány — a mindenkinek
                      szóló saját kitűzésem is ide tartozik); az archív nézettel kölcsönösen kizáró */}
                  <Button
                    variant="outline"
                    aria-pressed={pinnedView}
                    onClick={() =>
                      setFilters(
                        pinnedView
                          ? { pinned: '', direction: 'incoming' }
                          : { pinned: 'only', archived: '', direction: ALL },
                      )
                    }
                    className={pinnedView ? 'bg-accent text-foreground' : undefined}
                  >
                    <BookmarkIcon className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('messages.pinnedView')}</span>
                  </Button>
                  {/* „Mind olvasottnak" — fejléc-akció, minden méreten látszik (nem create). A gomb MÉRETE a
                      szomszédos „Új üzenet"-tel egyezik: két egymás melletti fejléc-akció nem lehet külön
                      magasságú.
                      A KIMENŐ nézetben MEG SEM JELENIK (user-döntés, 2026-08-25): azok az én kiküldött
                      üzeneteim — nincs mit „olvasottnak" jelölni rajtuk. */}
                  {direction !== 'outgoing' && (
                    <Button
                      variant="outline"
                      onClick={() => markAllReadMutation.mutate({})}
                      disabled={markAllReadMutation.isPending || !hasUnread}
                    >
                      <EnvelopeOpenIcon className="h-4 w-4" />
                      <span className="hidden sm:inline">{t('messages.markAllRead')}</span>
                    </Button>
                  )}
                  {/* Az „Új üzenet" gomb CSAK ≥md — mobilon a lebegő FAB (primaryAction) kezeli a létrehozást. */}
                  <div className="hidden md:block">
                    <ListNewButton
                      label={t('messages.new')}
                      onClick={() => setComposeOpen(true)}
                      enabled={canManage}
                      disabledTooltip={t('common.noCreatePermission')}
                    />
                  </div>
                </div>
              ) : undefined
            }
          />
        </div>
      }
    >
      <div className="flex h-full flex-col px-4 pb-2">
        <DataTable<MessageRow>
          columns={columns}
          rows={rows}
          getRowId={(m) => m.id}
          sort="date"
          order={order}
          onSort={toggleOrder}
          isLoading={!gated && (permsLoading || infinite.isLoading)}
          isError={isError}
          errorState={
            <ListErrorState forbidden={isForbidden} onRetry={() => void infinite.refetch()} />
          }
          emptyState={
            hasFilters ? (
              <EmptyState
                title={t('messages.noMatches')}
                description={t('messages.noMatchesDesc')}
              />
            ) : (
              <EmptyState title={t('messages.empty')} description={t('messages.emptyDesc')} />
            )
          }
          hasNextPage={infinite.hasNextPage}
          isFetchingNextPage={infinite.isFetchingNextPage}
          onLoadMore={loadMore}
          onRowClick={openDetail}
          rowActionsLabel={t('messages.actions.label')}
          actionsColWidth="11rem"
          rowActions={canView ? rowActions : undefined}
        />
      </div>

      {/* Üzenet-írás (compose) — jobb oldali FormSheet (messages.manage). */}
      <MessageComposeSheet open={composeOpen} onOpenChange={setComposeOpen} />

      {/* Kimenő szerkesztése (5 mező; sent-nél figyelmeztetés + revízió a BFF-en). */}
      <MessageEditSheet
        open={editTarget !== null}
        onOpenChange={(open) => !open && setEditTarget(null)}
        message={editTarget}
      />

      {/* Részletnézet (read-only; a nyitás mark-read-et indított a listából). */}
      <MessageDetailSheet
        open={detailTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDetailTarget(null);
            onClearDeepLink?.(); // deep-link: záráskor a `?open` param törlése (back-barát + újra-nyitható)
          }
        }}
        message={detailTarget}
        // A melléklet-KEZELÉS joga: jog ÉS tulajdon. Bejövő (nem általam küldött) üzenetnél a panel csak
        // letöltést kínál — csatolni/törölni nem lehet.
        // A melléklet-KEZELÉS joga: jog ÉS tulajdon (`mySub` = a session `sub`-ja). Bejövő (nem általam
        // küldött) üzenetnél a panel csak letöltést kínál — csatolni/törölni nem lehet. A fal a BFF-en van
        // (owner-check a melléklet-tx-ben), ez a felület-oldali kényelem.
        canManage={canManage && mySub !== null && detailTarget?.senderId === mySub}
      />

      {/* „Küldés most" megerősítés — draft/scheduled → sent (OCC). */}
      <ConfirmDialog
        open={sendTarget !== null}
        onOpenChange={(open) => !open && setSendTarget(null)}
        title={t('messages.sendNow.title')}
        description={t('messages.sendNow.desc', { subject: sendTarget?.subject ?? '' })}
        confirmLabel={t('messages.sendNow.confirm')}
        cancelLabel={t('messages.cancel')}
        pendingLabel={t('messages.sendNow.working')}
        tone="default"
        pending={sendMutation.isPending}
        onConfirm={confirmSend}
      />

      {/* Időzítés / átidőzítés — datetime-mezős dedikált dialógus. */}
      <MessageScheduleDialog
        open={scheduleTarget !== null}
        onOpenChange={(open) => !open && setScheduleTarget(null)}
        subject={scheduleTarget?.subject ?? ''}
        currentScheduledAt={scheduleTarget?.scheduledAt ?? null}
        pending={scheduleMutation.isPending}
        onConfirm={confirmSchedule}
      />

      {/* Időzítés visszavonása — scheduled → draft (409 = a dispatcher megelőzte). */}
      <ConfirmDialog
        open={unscheduleTarget !== null}
        onOpenChange={(open) => !open && setUnscheduleTarget(null)}
        title={t('messages.unschedule.title')}
        description={t('messages.unschedule.desc', { subject: unscheduleTarget?.subject ?? '' })}
        confirmLabel={t('messages.unschedule.confirm')}
        cancelLabel={t('messages.cancel')}
        pendingLabel={t('messages.unschedule.working')}
        tone="warning"
        pending={unscheduleMutation.isPending}
        onConfirm={confirmUnschedule}
      />
    </AppShell>
  );
}
